import type { IconType } from "react-icons";
import { BsFillPassportFill, BsPeopleFill } from "react-icons/bs";
import {
  HiArchiveBox,
  HiArrowTrendingUp,
  HiBeaker,
  HiHome,
  HiIdentification,
  HiInbox,
  HiMiniBuildingOffice,
  HiShieldCheck,
  HiSquares2X2,
  // HiDocumentCheck,
  // HiScale,
  // HiShare,
  // HiClipboardDocumentList,
  // HiTruck,
  // HiWrenchScrewdriver,
} from "react-icons/hi2";
import { IoLayers } from "react-icons/io5";
import {
  MdHelp,
  // MdRecycling
} from "react-icons/md";
import { RiSendPlaneFill } from "react-icons/ri";

// import { TbCertificate } from "react-icons/tb";

import { batteryMfgPaths, cellMfgPaths } from "@/features/Manufacturing/paths";
// import { recallPaths } from "@/features/Recall/paths";
import { warrantyPaths } from "@/features/Warranty/paths";
import type { OrgType } from "@/types/orgTypes";
import {
  ALL_MFR_TYPES,
  BATTERY_MFR_TYPES,
  CELL_MFR_TYPES,
  EPR_ELIGIBLE_TYPES,
  // INTEGRATION_TYPES,
  // RECYCLER_TYPES,
  // REFURBISHER_TYPES,
  WRITE_CAPABLE_TYPES,
} from "@/types/orgTypes";

export interface NavSubItem {
  name: string;
  url: string;
  icon?: IconType;
  orgTypes?: readonly OrgType[];
}

export interface NavItem {
  name: string;
  url: string;
  icon: IconType;
  badge?: string;
  orgTypes?: readonly OrgType[];
  roles?: readonly ("admin" | "member" | "owner" | "guest-member")[];
  items?: NavSubItem[];
}

export interface NavConfig {
  navHead: NavItem[];
  navMain: NavItem[];
  navTail: NavItem[];
}

export function getNavConfig(unreadCount: number): NavConfig {
  return {
    navHead: [
      {
        name: "Inbox",
        url: "/inbox",
        icon: HiInbox,
        badge: unreadCount > 0 ? String(unreadCount) : undefined,
      },
    ],

    navMain: [
      {
        name: "Overview",
        url: "/overview",
        icon: HiHome,
      },
      {
        name: "Battery Lifecycle",
        url: "/digital-identity",
        icon: BsFillPassportFill,
        items: [
          {
            name: "Passports",
            url: "/digital-identity",
          },
          {
            name: "Investigate",
            url: warrantyPaths.investigate(),
            orgTypes: ALL_MFR_TYPES,
          },
          {
            name: "Warranty Claims",
            url: warrantyPaths.claims(),
            orgTypes: ALL_MFR_TYPES,
          },
          {
            name: "Warranty Exposure",
            url: warrantyPaths.exposure(),
            orgTypes: ALL_MFR_TYPES,
          },
          {
            name: "Spec Thresholds",
            url: warrantyPaths.thresholds(),
            orgTypes: ALL_MFR_TYPES,
          },
          // {
          //   name: "Recall Campaigns",
          //   url: recallPaths.campaigns(),
          //   orgTypes: ALL_MFR_TYPES,
          // },
          // {
          //   name: "Trace a Battery",
          //   url: recallPaths.trace(),
          //   orgTypes: ALL_MFR_TYPES,
          // },
        ],
      },

      // Tier 1 — Manufacturing
      {
        name: "Models",
        url: "/models",
        icon: IoLayers,
        orgTypes: ALL_MFR_TYPES,
      },
      {
        name: "Manufacturing",
        url: "/manufacturing",
        icon: HiBeaker,
        orgTypes: ALL_MFR_TYPES,
        items: [
          {
            name: "Cell Production Lots",
            url: cellMfgPaths.lots(),
            orgTypes: CELL_MFR_TYPES,
          },
          {
            name: "Battery Pack Lots",
            url: batteryMfgPaths.lots(),
            orgTypes: BATTERY_MFR_TYPES,
          },
          {
            name: "Workflow Templates",
            url: "/manufacturing/workflows",
            orgTypes: ALL_MFR_TYPES,
          },
          {
            name: "Thresholds & Grade Bins",
            url: cellMfgPaths.quality(),
            orgTypes: CELL_MFR_TYPES,
          },
          {
            name: "Scrap & Waste",
            url: cellMfgPaths.scrap(),
            orgTypes: CELL_MFR_TYPES,
          },
          {
            name: "Yield Analytics",
            url: cellMfgPaths.yieldAnalytics(),
            orgTypes: CELL_MFR_TYPES,
          },
          {
            name: "Lineage Trace",
            url: cellMfgPaths.trace(),
            orgTypes: CELL_MFR_TYPES,
          },
        ],
      },

      {
        name: "Inventory",
        url: "/inventory",
        icon: HiArchiveBox,
        orgTypes: ALL_MFR_TYPES,
        items: [
          {
            name: "Materials Inventory",
            url: "/inventory/materials",
          },
          {
            name: "Product Inventory",
            url: "/inventory/product",
          },
          {
            name: "Dispatches",
            url: "/inventory/dispatches",
          },
        ],
      },

      // Tier 2 — Integration (Phase 2)
      // {
      //   name: "Vehicle Binding",
      //   url: "/vehicle-binding",
      //   icon: HiTruck,
      //   orgTypes: INTEGRATION_TYPES,
      // },

      // Tier 3 — Operations (Phase 2)
      // {
      //   name: "Service Events",
      //   url: "/service-events",
      //   icon: HiWrenchScrewdriver,
      //   orgTypes: ["service_provider"],
      // },
      // {
      //   name: "Fleet Telemetry",
      //   url: "/fleet-telemetry",
      //   icon: HiArrowTrendingUp,
      //   orgTypes: ["fleet_operator"],
      // },

      // Tier 4 — End-of-Life (Phase 2)
      // {
      //   name: "Recycling Records",
      //   url: "/recycling-records",
      //   icon: MdRecycling,
      //   orgTypes: RECYCLER_TYPES,
      // },
      // {
      //   name: "Second Life Passports",
      //   url: "/second-life-passports",
      //   icon: BsFillPassportFill,
      //   orgTypes: REFURBISHER_TYPES,
      // },

      // Tier 5 — Oversight (Phase 2)
      // {
      //   name: "Certifications",
      //   url: "/certifications",
      //   icon: TbCertificate,
      //   orgTypes: ["certification_lab"],
      // },
      // {
      //   name: "Regulatory Access",
      //   url: "/regulatory-access",
      //   icon: HiDocumentCheck,
      //   orgTypes: ["regulatory_body"],
      // },

      // Compliance
      {
        name: "EPR Compliance",
        url: "/epr",
        icon: HiShieldCheck,
        orgTypes: EPR_ELIGIBLE_TYPES,
        items: [
          {
            name: "Overview",
            url: "/epr/overview",
            icon: HiSquares2X2,
          },
          {
            name: "POM Data",
            url: "/epr/pom",
            icon: HiArchiveBox,
          },
          // {
          //   name: "Obligations",
          //   url: "/epr/obligations",
          //   icon: HiScale,
          // },
          // {
          //   name: "Compliance Status",
          //   url: "/epr/compliance",
          //   icon: HiDocumentCheck,
          // },
          {
            name: "Projections",
            url: "/epr/projections",
            icon: HiArrowTrendingUp,
          },
        ],
      },

      // Network
      {
        name: "Partners",
        url: "/partners",
        icon: BsPeopleFill,
        // Visible to all except oversight types
        orgTypes: WRITE_CAPABLE_TYPES,
      },
      {
        name: "Access Requests",
        url: "/access-requests",
        icon: HiIdentification,
      },

      // Org settings (admin/owner only)
      {
        name: "Organization",
        url: "/organization",
        icon: HiMiniBuildingOffice,
        roles: ["admin", "owner"] as const,
        items: [
          {
            name: "Details",
            url: "/organization/details",
            icon: HiMiniBuildingOffice,
          },
          {
            name: "Members",
            url: "/organization/members",
            icon: BsPeopleFill,
          },
          // {
          //   name: "Factories",
          //   url: "/organization/factories",
          //   icon: HiArchiveBox,
          // },
        ],
      },
    ],

    navTail: [
      {
        name: "Support",
        url: "#",
        icon: MdHelp,
      },
      {
        name: "Feedback",
        url: "#",
        icon: RiSendPlaneFill,
      },
    ],
  };
}
